import { useState, type ReactNode } from 'react'
import { Layout, Menu, Typography, Button, Space, theme, Modal, Tooltip } from 'antd'
import {
  DashboardOutlined,
  BarChartOutlined,
  LogoutOutlined,
  UserOutlined,
  ApartmentOutlined,
  SettingOutlined,
  TeamOutlined,
  BankOutlined,
  AuditOutlined,
  AppstoreOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  LineChartOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth'
import { useThemeStore } from '@/store/theme'
import { useCapabilitiesStore } from '@/store/capabilities'
import { useImpersonationStore } from '@/store/impersonation'
import { useCapabilities } from '@/hooks/useCapabilities'
import { ThemeSwitcher } from '@/components/ThemeSwitcher'
import { OrgSwitcher } from '@/components/OrgSwitcher'
import { ImpersonationBanner } from '@/components/ImpersonationBanner'
import { ROLE_LABELS } from '@/utils/userRoles'

const { Header, Sider, Content } = Layout
const { Text } = Typography

interface MainLayoutProps {
  children: ReactNode
}

function MainLayout({ children }: MainLayoutProps) {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user, logout } = useAuthStore()
  const resolved = useThemeStore((s) => s.resolved)
  const { token: t } = theme.useToken()
  const capabilities = useCapabilities()
  const hasAdminShellAccess = capabilities.adminAccess

  const handleLogout = () => {
    Modal.confirm({
      title: 'Завершить сеанс?',
      content: 'Локальная сессия будет завершена, после чего браузер выполнит выход из единого контура авторизации.',
      okText: 'Выйти',
      cancelText: 'Отмена',
      onOk: async () => {
        queryClient.clear()
        useCapabilitiesStore.getState().resetCapabilities()
        useImpersonationStore.setState({ impersonatedOrgId: null, impersonatedOrgName: null })
        await logout()
      },
    })
  }

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: 'Дашборд',
      onClick: () => navigate('/'),
    },
    {
      key: '/org',
      icon: <ApartmentOutlined />,
      label: 'Оргструктура',
      children: [
        {
          key: '/org/structure',
          label: 'Структура отделений',
          onClick: () => navigate('/org/structure'),
        },
        {
          key: '/org/bed-fund',
          label: 'Коечный фонд',
          onClick: () => navigate('/org/bed-fund'),
        },
        {
          key: '/org/refs',
          label: 'Справочники',
          onClick: () => navigate('/org/refs'),
        },
        {
          key: '/org/configurator',
          label: 'Конфигуратор',
          onClick: () => navigate('/org/configurator'),
        },
        {
          key: '/org/recviz-result',
          label: 'Справочник реквизитов',
          onClick: () => navigate('/org/recviz-result'),
        },
      ],
    },
    {
      key: '/api/v1/mur/view',
      icon: <AuditOutlined />,
      label: <a href="/api/v1/mur/view">Взаиморасчёты МУР</a>,
    },
    {
      key: 'mur-lite-cover',
      icon: <LineChartOutlined />,
      label: (
        <a
          href="https://bi.medcifra.ru/dashboards/jmkyd0edqqj02"
          target="_blank"
          rel="noreferrer"
        >
          Взаиморасчёты МУР (лёгкий)
        </a>
      ),
    },
    {
      key: '/analytics',
      icon: <BarChartOutlined />,
      label: 'Аналитика',
      disabled: true,
    },
    // Администрирование — показывается только если есть adminAccess
    ...(hasAdminShellAccess
      ? [
          {
            key: 'admin-section',
            icon: <SettingOutlined />,
            label: 'Админка',
            children: [
              {
                key: '/admin',
                icon: <AppstoreOutlined />,
                label: 'Сводка',
                onClick: () => navigate('/admin'),
              },
              ...(capabilities.orgsRead
                ? [
                    {
                      key: '/admin/orgs',
                      icon: <BankOutlined />,
                      label: 'Организации',
                      onClick: () => navigate('/admin/orgs'),
                    },
                  ]
                : []),
              ...(capabilities.platformRbacRead
                ? [
                    {
                      key: '/admin/platform-rbac',
                      icon: <SafetyOutlined />,
                      label: 'Роли и права',
                      onClick: () => navigate('/admin/platform-rbac'),
                    },
                  ]
                : []),
              {
                key: '/admin/users',
                icon: <TeamOutlined />,
                label: 'Сотрудники',
                onClick: () => navigate('/admin/users'),
              },
              {
                key: 'admin-bi-access',
                icon: <DatabaseOutlined />,
                label: 'BI и доступы',
                children: [
                  {
                    key: '/admin/bi-workspace',
                    icon: <DatabaseOutlined />,
                    label: 'BI-центр',
                    onClick: () => navigate('/admin/bi-workspace'),
                  },
                  {
                    key: '/admin/access-workspace',
                    icon: <AppstoreOutlined />,
                    label: 'Люди и RLS',
                    onClick: () => navigate('/admin/access-workspace'),
                  },
                  {
                    key: '/admin/dashboard-access-workspace',
                    icon: <AppstoreOutlined />,
                    label: 'Видимость',
                    onClick: () => navigate('/admin/dashboard-access-workspace'),
                  },
                  {
                    key: '/admin/access-groups',
                    icon: <TeamOutlined />,
                    label: 'Группы',
                    onClick: () => navigate('/admin/access-groups'),
                  },
                  {
                    key: '/admin/access-health',
                    icon: <SafetyOutlined />,
                    label: 'Контроль',
                    onClick: () => navigate('/admin/access-health'),
                  },
                  ...(capabilities.superadminAccess
                    ? [
                        {
                          key: '/admin/dashboard-template-registry',
                          icon: <AppstoreOutlined />,
                          label: 'Шаблоны',
                          onClick: () => navigate('/admin/dashboard-template-registry'),
                        },
                        {
                          key: '/admin/dashboard-publications',
                          icon: <AppstoreOutlined />,
                          label: 'Публикации',
                          onClick: () => navigate('/admin/dashboard-publications'),
                        },
                      ]
                    : []),
                ],
              },
              {
                key: '/admin/audit',
                icon: <AuditOutlined />,
                label: 'Журнал аудита',
                onClick: () => navigate('/admin/audit'),
              },
              // Только для superadmin
              ...(capabilities.superadminAccess
                ? [
                    {
                      key: '/admin/nsi-tfoms-profiles',
                      label: 'Профили ТФОМС',
                      onClick: () => navigate('/admin/nsi-tfoms-profiles'),
                    },
                    {
                      key: '/admin/base-tariffs',
                      label: 'Базовые тарифы',
                      onClick: () => navigate('/admin/base-tariffs'),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ]

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme={resolved === 'dark' ? 'dark' : 'light'}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: collapsed ? '0 8px' : '0 16px',
            borderBottom: `1px solid ${t.colorBorderSecondary}`,
          }}
        >
          <img
            src={collapsed ? '/logo-icon.svg' : '/logo.svg'}
            alt="Медцифра"
            style={{
              height: collapsed ? 32 : 28,
              width: 'auto',
              transition: 'all 0.2s',
            }}
          />
        </div>

        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={['admin-section', 'admin-bi-access']}
          items={menuItems}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <Layout style={{ marginLeft: collapsed ? 80 : 200 }}>
        <Header
          id="platform-context"
          aria-label="Рабочая организация и сеанс"
          style={{
            background: t.colorBgContainer,
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            borderBottom: `1px solid ${t.colorBorderSecondary}`,
          }}
        >
          <Space>
            {capabilities.impersonate && <OrgSwitcher />}
            <ThemeSwitcher />
            <UserOutlined />
            <Tooltip title={user?.email || ''}>
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
                <Text>{user?.fullName || 'Сотрудник'}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {user?.email || ''}
                </Text>
              </div>
            </Tooltip>
            <Text type="secondary">({ROLE_LABELS[user?.role ?? ''] || user?.role})</Text>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              Выход
            </Button>
          </Space>
        </Header>

        <ImpersonationBanner />

        <Content
          style={{
            margin: '24px 16px',
            padding: 24,
            background: t.colorBgContainer,
            minHeight: 280,
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  )
}

export default MainLayout
